import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../../layout/Card.jsx';
import { Icon } from '../../layout/Icon.jsx';
import { api, apiError } from '../../shared/api.js';
import { useAuth } from '../../shared/useAuth.js';
import { formatIQD } from '../../shared/format.js';
import { toast } from '../../shared/useToast.js';

import { DepartmentTabs } from './components/DepartmentTabs.jsx';
import { CategoryFilter } from './components/CategoryFilter.jsx';
import { ProductGrid } from './components/ProductGrid.jsx';
import { Cart } from './components/Cart.jsx';
import { PaymentPanel } from './components/PaymentPanel.jsx';
import { ChildLinkPicker } from './components/ChildLinkPicker.jsx';
import { CardScanInPos } from './components/CardScanInPos.jsx';
import { BarcodeInput } from './components/BarcodeInput.jsx';
import { DiscountModal } from './components/DiscountModal.jsx';
import { InvoicePreviewModal } from './components/InvoicePreviewModal.jsx';
import { SuspendBanner } from './components/SuspendBanner.jsx';
import { TodaySalesModal } from './components/TodaySalesModal.jsx';
import { CancelInvoiceModal } from './components/CancelInvoiceModal.jsx';
import { usePosState } from './hooks/usePosState.js';
import { useWarehouseByType } from './hooks/useWarehouses.js';
import { useQuickProducts, useCategoriesByWarehouse } from './hooks/useProducts.js';
import { useCreateSale } from './hooks/useCreateSale.js';

export default function POSScreen() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canCafeteria = hasPermission('pos.cafeteria.sell');
  const canToys = hasPermission('pos.toys.sell');
  const canDiscount = hasPermission('pos.discount');
  // الإلغاء إجراء إرجاع مالي — نفس الصلاحية التي يحرس بها الخادم المسار،
  // فلا نُحدث مفتاحاً جديداً يقفل الباب على المستخدمين حتى يُعيدوا تسجيل الدخول.
  const canRefund = hasPermission('pos.refund');

  const dept = usePosState((s) => s.dept);
  const setDept = usePosState((s) => s.setDept);
  const cart = usePosState((s) => s.cart);
  const invoiceDiscount = usePosState((s) => s.invoiceDiscount);
  const link = usePosState((s) => s.link);
  const payment = usePosState((s) => s.payment);
  const setPayment = usePosState((s) => s.setPayment);
  const addProduct = usePosState((s) => s.addProduct);
  const clearCart = usePosState((s) => s.clearCart);
  const subtotalFn = usePosState((s) => s.subtotal);
  const grandTotalFn = usePosState((s) => s.grandTotal);
  const resetAfterSale = usePosState((s) => s.resetAfterSale);

  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState(null);
  const [showChildPicker, setShowChildPicker] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);
  const [lastInvoice, setLastInvoice] = useState(null);
  const [salesOpen, setSalesOpen] = useState(false);
  // الفاتورة المطلوب إلغاؤها — تُفتح من قائمة فواتير اليوم أو من معاينة البيع.
  const [cancelTarget, setCancelTarget] = useState(null);

  // Default-pick first available department
  useEffect(() => {
    if (dept === 'cafeteria' && !canCafeteria && canToys) setDept('toys');
    else if (dept === 'toys' && !canToys && canCafeteria) setDept('cafeteria');
  }, [canCafeteria, canToys, dept, setDept]);

  const { warehouse } = useWarehouseByType(dept);
  const { data: categories = [] } = useCategoriesByWarehouse(warehouse?._id);
  const { data: products = [], isLoading: productsLoading } = useQuickProducts({ dept, q: search });

  const filteredProducts = useMemo(() => {
    if (!activeCat) return products;
    return products.filter((p) => String(p.categoryId) === String(activeCat));
  }, [products, activeCat]);

  const me = useAuth((s) => s.user);
  const assignedCashboxId = me?.assignedCashboxId?._id || me?.assignedCashboxId || null;

  // Cashbox auto-pick by dept type
  const cashboxQuery = useQuery({
    queryKey: ['cashboxes', dept],
    queryFn: async () => {
      const { data } = await api.get('/cashbox', { params: { type: dept, active: true } });
      return data?.items || data?.cashboxes || data?.data || [];
    }
  });
  const cashboxes = cashboxQuery.data || [];

  useEffect(() => {
    if (!cashboxes.length) return;
    const valid = cashboxes.some((cb) => String(cb._id || cb.id) === String(payment.cashboxId));
    if (!valid) {
      // إن كان للمستخدم قاصة منسبة وتظهر في القائمة الحالية، اختَرها افتراضياً
      const preferred = assignedCashboxId
        ? cashboxes.find((cb) => String(cb._id || cb.id) === String(assignedCashboxId))
        : null;
      const pick = preferred || cashboxes[0];
      setPayment({ cashboxId: pick._id || pick.id });
    }
  }, [cashboxes, assignedCashboxId]); // eslint-disable-line react-hooks/exhaustive-deps

  const subtotal = subtotalFn();
  const grandTotal = grandTotalFn();

  const createSale = useCreateSale();

  // No permissions at all → empty state
  if (!canCafeteria && !canToys) {
    return (
      <Card>
        <div className="text-center py-10 text-ink-3 flex flex-col items-center gap-2">
          <Icon name="pos" size={42} color="#D6CCB3" />
          <div className="font-bold text-[14px]">لا تملك صلاحيات البيع</div>
          <div className="text-[12px]">يحتاج المستخدم إلى صلاحية pos.cafeteria.sell أو pos.toys.sell</div>
        </div>
      </Card>
    );
  }

  // سبب المنع نصّاً، كي تظهر رسالة مفهومة بدل «تحقق من الفاتورة» العامة.
  function submitBlockedReason() {
    if (cart.length === 0) return 'السلة فارغة';
    if (!payment.method) return 'اختر طريقة الدفع';
    if (payment.method === 'on_session' && !link?.sessionId) {
      return 'التعليق على الجلسة يحتاج جلسة دخول فعلية — استخدم رصيد البطاقة';
    }
    if (payment.method === 'card_balance') {
      if (!link?.cardUid) return 'لا توجد بطاقة مرتبطة بهذا الطفل — اختر طريقة دفع أخرى';
      if (link?.cardBalance != null && link.cardBalance < grandTotal) {
        return `رصيد البطاقة غير كافٍ لإتمام عملية الشراء — المتوفر ${formatIQD(link.cardBalance)} والمطلوب ${formatIQD(grandTotal)}`;
      }
    }
    return null;
  }

  function canSubmit() {
    return !submitBlockedReason();
  }

  async function handleSubmit() {
    const blocked = submitBlockedReason();
    if (blocked) {
      toast.error(blocked);
      return;
    }
    // اربط الفاتورة النقدية بالقاصة (المنسبة للمستخدم أولاً، ثم المحددة في payment) ليظهر
    // الإيراد في snapshot قفل اليوم لتلك القاصة.
    const cashboxForInvoice = payment.method === 'cash'
      ? (payment.cashboxId || assignedCashboxId || (cashboxes[0]?._id || cashboxes[0]?.id) || undefined)
      : undefined;

    const body = {
      posType: dept,
      items: cart.map((it) => ({
        productId: it.productId,
        qty: it.qty,
        unitPrice: it.unitPrice,
        discount: it.discount || 0
      })),
      discountTotal: invoiceDiscount,
      paymentMethod: payment.method,
      paidAmount: payment.method === 'on_session' ? 0 : grandTotal,
      cashboxId: cashboxForInvoice,
      sessionId: link?.sessionId || undefined,
      cardUid: link?.cardUid || undefined,
      childSearchId: link?.linkedBy === 'child_search' ? link?.childId : undefined,
      linkedBy: link?.linkedBy || 'direct'
    };
    try {
      const invoice = await createSale.mutateAsync(body);
      toast.success(`تم البيع بنجاح • فاتورة ${invoice.number}`);
      setLastInvoice(invoice);
    } catch (err) {
      toast.error(apiError(err));
    }
  }

  return (
    <div className="space-y-3.5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <DepartmentTabs
          value={dept}
          onChange={setDept}
          canCafeteria={canCafeteria}
          canToys={canToys}
        />
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" className="btn" onClick={() => setSalesOpen(true)}>
            <Icon name="reports" size={14} /> فواتير اليوم
          </button>
          {canDiscount ? (
            <button
              type="button"
              className="btn"
              onClick={() => setShowDiscount(true)}
              disabled={cart.length === 0}
            >
              <Icon name="pricing" size={14} /> خصم على الفاتورة
              {invoiceDiscount > 0 ? <span className="text-orange-deep ms-1">({formatIQD(invoiceDiscount)})</span> : null}
            </button>
          ) : null}
        </div>
      </div>

      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: 'minmax(0,1fr) 400px' }}
      >
        {/* Left: search/scan toolbar + categories + product grid */}
        <div className="space-y-3 min-w-0">
          <div className="card">
            <div className="card-b flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[180px]">
                <Icon
                  name="search"
                  size={14}
                  className="absolute top-1/2 -translate-y-1/2 right-3 text-ink-3 pointer-events-none"
                />
                <input
                  className="input pr-9 text-[12.5px]"
                  placeholder="ابحث عن منتج بالاسم أو الـ SKU..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <BarcodeInput onResolved={(p) => addProduct(p)} />
            </div>
          </div>

          <CategoryFilter categories={categories} value={activeCat} onChange={setActiveCat} />

          <ProductGrid
            products={filteredProducts}
            loading={productsLoading}
            onAdd={addProduct}
          />
        </div>

        {/* Right: cart side panel */}
        <div className="space-y-3 sticky top-[88px] self-start">
          <SuspendBanner />

          <div className="card flex flex-col" style={{ maxHeight: 'calc(100vh - 140px)' }}>
            <div className="card-h">
              <div className="flex-1">
                <h3 className="text-[14.5px] font-bold">الفاتورة الحالية</h3>
                <div className="text-xs text-ink-3 mt-0.5">
                  {dept === 'cafeteria' ? 'الكافتيريا' : 'محل الألعاب'}
                </div>
              </div>
              {cart.length > 0 ? (
                <button
                  type="button"
                  className="btn btn-ghost text-red"
                  onClick={clearCart}
                  title="إفراغ السلة"
                >
                  <Icon name="trash" size={14} />
                </button>
              ) : null}
            </div>

            <div className="card-b flex flex-col gap-2.5 overflow-hidden">
              <div className="flex items-center gap-2 flex-wrap">
                <CardScanInPos />
                <button
                  type="button"
                  className="btn"
                  onClick={() => setShowChildPicker(true)}
                >
                  <Icon name="search" size={14} /> بحث طفل
                </button>
              </div>

              <div className="flex-1 overflow-auto -mx-1 px-1" style={{ minHeight: 100 }}>
                <Cart />
              </div>

              <div className="bg-paper-2 rounded-[12px] p-3 space-y-1">
                <div className="flex justify-between text-[12.5px]">
                  <span className="text-ink-2">المجموع الفرعي</span>
                  <b>{formatIQD(subtotal)}</b>
                </div>
                {invoiceDiscount > 0 ? (
                  <div className="flex justify-between text-[12.5px] text-red">
                    <span>خصم على الفاتورة</span>
                    <b>-{formatIQD(invoiceDiscount)}</b>
                  </div>
                ) : null}
                <div className="border-t border-line pt-1.5 flex justify-between items-center">
                  <span className="font-bold">الإجمالي</span>
                  <span className="text-[22px] font-extrabold text-orange-deep">
                    {formatIQD(grandTotal)}
                  </span>
                </div>
              </div>

              <PaymentPanel grandTotal={grandTotal} />

              <button
                type="button"
                className="btn btn-primary btn-xl justify-center w-full"
                onClick={handleSubmit}
                disabled={!canSubmit() || createSale.isPending}
              >
                <Icon name="check" size={16} />
                {createSale.isPending ? 'جاري الحفظ...' : `إتمام البيع • ${formatIQD(grandTotal)}`}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ChildLinkPicker open={showChildPicker} onClose={() => setShowChildPicker(false)} />
      <DiscountModal open={showDiscount} onClose={() => setShowDiscount(false)} />
      <InvoicePreviewModal
        invoice={lastInvoice}
        onClose={() => setLastInvoice(null)}
        onContinue={() => {
          resetAfterSale({ keepLink: true });
          setLastInvoice(null);
        }}
        onNew={() => {
          resetAfterSale({ keepLink: false });
          setLastInvoice(null);
        }}
        onRequestCancel={canRefund ? (inv) => setCancelTarget(inv) : null}
      />

      <TodaySalesModal
        open={salesOpen}
        onClose={() => setSalesOpen(false)}
        canCancel={canRefund}
        onRequestCancel={(inv) => setCancelTarget(inv)}
      />

      <CancelInvoiceModal
        invoice={cancelTarget}
        onClose={() => setCancelTarget(null)}
        onCancelled={(cancelled) => {
          // الفاتورة التي أُلغيت هي نفسها المعروضة في المعاينة: أغلِق المعاينة
          // وأفرغ السلة، فلا يبقى على الشاشة وصل بيع لم يعد قائماً.
          const id = cancelled && (cancelled._id || cancelled.id);
          if (lastInvoice && id && String(lastInvoice._id || lastInvoice.id) === String(id)) {
            setLastInvoice(null);
            resetAfterSale({ keepLink: false });
          }
        }}
      />
    </div>
  );
}
